import { type ParsedUrlQuery } from 'querystring';

import type { QuizPlanOrderResponseIntent } from '@/api/gen/_model/quizPlanOrderResponseIntent';
import { VALIDATION_RULES } from '@/shared/utils/CheckoutSchema';
import type { PlanSourceType } from '@/types';
import { parseTrackingId } from '@/utils/analytics/trackingId';

import { PAYMENT_URLS } from './paymentUrls';

export type PaidPlanType = 'BASIC' | 'PRO';
export type PlanPaymentIntent = QuizPlanOrderResponseIntent;

export const supportsPaidExtension = (
  sourceType: PlanSourceType | undefined,
): boolean =>
  sourceType === 'DIRECT_PURCHASE' ||
  sourceType === 'ADMIN_GRANT' ||
  sourceType === 'EVENT_GRANT';

export type PlanChangeTarget =
  | { kind: 'active' }
  | { kind: 'specific'; quizPlanHashId: string };

export type PaymentEntry =
  | {
      kind: 'purchase';
      plan: PaidPlanType;
      startDate?: string;
      school?: boolean;
      initialStartDate?: string;
      monthCount?: number;
      userCount?: number;

      checkoutId?: string;
      impressionId?: string;
      trackingCountryCode?: string;
    }
  | {
      kind: 'extend' | 'upgrade';
      target: PlanChangeTarget;
    };

export type PaymentEntryParseResult =
  | { ok: true; entry: PaymentEntry }
  | { ok: false };

const CHECKOUT_PATH_BY_KIND: Record<PaymentEntry['kind'], string> = {
  purchase: PAYMENT_URLS.CHECKOUT,
  extend: PAYMENT_URLS.EXTEND,
  upgrade: PAYMENT_URLS.UPGRADE,
};

const isPaidPlan = (value: unknown): value is PaidPlanType =>
  value === 'BASIC' || value === 'PRO';

export const isPlanPaymentIntent = (
  value: unknown,
): value is PlanPaymentIntent =>
  value === 'NEW_PURCHASE' ||
  value === 'FUTURE_PURCHASE' ||
  value === 'EXTEND' ||
  value === 'UPGRADE';

export const resolveOrderIntent = (
  serverIntent: unknown,
): PlanPaymentIntent | null =>
  isPlanPaymentIntent(serverIntent) ? serverIntent : null;

const isIsoLocalDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

const getTodayLocalDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

/**
 * 결제 대상 플랜 해시의 유효성 판정. URL·query 로 들어온 값을 신뢰하기 전의
 * 단일 관문이다 — 같은 정규식이 여러 곳에 복제되면 한쪽만 바뀌었을 때 target 이
 * 조용히 유실되므로 반드시 이 함수를 공유한다.
 */
export const isValidPlanHashId = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length <= 100 &&
  /^[A-Za-z0-9]+$/.test(value);

const parseTarget = (value: unknown): PlanChangeTarget | null => {
  if (value === undefined) return { kind: 'active' };
  if (!isValidPlanHashId(value)) {
    return null;
  }
  return { kind: 'specific', quizPlanHashId: value };
};

export const parsePaymentEntryQuery = (
  query: ParsedUrlQuery,
): PaymentEntryParseResult => {
  if (
    query.intent !== undefined ||
    Array.isArray(query.kind) ||
    Array.isArray(query.plan) ||
    Array.isArray(query.start) ||
    Array.isArray(query.target)
  ) {
    return { ok: false };
  }

  const kind =
    query.kind ?? (query.plan === undefined ? undefined : 'purchase');
  if (kind === 'purchase') {
    if (!isPaidPlan(query.plan)) return { ok: false };
    if (
      query.start !== undefined &&
      (!isIsoLocalDate(query.start) || query.start < getTodayLocalDate())
    ) {
      return { ok: false };
    }
    return {
      ok: true,
      entry: {
        kind,
        plan: query.plan,
        ...(typeof query.school_country === 'string' &&
          /^[A-Z]{2}$/.test(query.school_country) && {
            trackingCountryCode: query.school_country,
          }),
        ...(parseTrackingId(query.checkout_id) && {
          checkoutId: parseTrackingId(query.checkout_id),
        }),
        ...(parseTrackingId(query.impression_id) && {
          impressionId: parseTrackingId(query.impression_id),
        }),
        ...(query.start && { startDate: query.start }),
        ...(query.school === 'true' && { school: true }),
        ...(isIsoLocalDate(query.quoteStart) &&
          query.quoteStart >= getTodayLocalDate() && {
            initialStartDate: query.quoteStart,
          }),
        ...(typeof query.months === 'string' &&
          /^\d+$/.test(query.months) &&
          Number(query.months) >= 1 &&
          Number(query.months) <= 60 && { monthCount: Number(query.months) }),
        ...(typeof query.users === 'string' &&
          /^\d+$/.test(query.users) &&
          Number(query.users) >= 1 &&
          Number(query.users) <= VALIDATION_RULES.NUMBER_OF_USERS.MAX && {
            userCount: Number(query.users),
          }),
      },
    };
  }

  if (kind === 'extend' || kind === 'upgrade') {
    const target = parseTarget(query.target);
    return target ? { ok: true, entry: { kind, target } } : { ok: false };
  }

  return { ok: false };
};

export const createPaymentEntrySearchParams = (entry: PaymentEntry) => {
  const params = new URLSearchParams();
  if (entry.kind === 'purchase') {
    params.set('plan', entry.plan);
    if (entry.checkoutId) params.set('checkout_id', entry.checkoutId);
    if (entry.impressionId) params.set('impression_id', entry.impressionId);
    if (entry.trackingCountryCode)
      params.set('school_country', entry.trackingCountryCode);
    if (entry.startDate) params.set('start', entry.startDate);
    if (entry.school) params.set('school', 'true');
    if (entry.initialStartDate)
      params.set('quoteStart', entry.initialStartDate);
    if (entry.monthCount != null && entry.monthCount !== 0)
      params.set('months', String(entry.monthCount));
    if (entry.userCount != null && entry.userCount !== 0)
      params.set('users', String(entry.userCount));
  } else if (entry.target.kind === 'specific') {
    params.set('target', entry.target.quizPlanHashId);
  }
  return params;
};

export const getCheckoutUrl = (entry: PaymentEntry) => {
  const query = createPaymentEntrySearchParams(entry).toString();
  const path = CHECKOUT_PATH_BY_KIND[entry.kind];
  return query ? `${path}?${query}` : path;
};
